import { TextCodes } from '@api/types/translation/TextCodes';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  TextField,
  TextFieldProps,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { t } from '@translations';
import { Ref, forwardRef, useMemo, useState } from 'react';

export type DropdownOption<T> = {
  value: T;
  label: string;
  action?: any;
  icon?: React.ReactNode;
  isSelected?: boolean;
};

export type DropdownProps<T> = TextFieldProps & {
  options: DropdownOption<T>[];
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  getValue?: (value: T | T[]) => void;
  withRemoveButton?: boolean;
  value?: T | T[];
  readOnly?: boolean;
  dropdownZIndex?: number;
  isLoading?: boolean;
};

export const DropdownField = forwardRef<HTMLInputElement, DropdownProps<any>>(
  <T,>(
    {
      name,
      value: filterValue,
      label,
      placeholder,
      options,
      multiple,
      getValue = () => null,
      withRemoveButton,
      readOnly,
      dropdownZIndex = 2,
      isLoading,
      ...props
    }: DropdownProps<T>,
    ref: Ref<HTMLInputElement>
  ) => {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? `${name}-select` : undefined;
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isChecked = (option: T) => Array.isArray(filterValue) && filterValue?.includes(option);

    const displayValue = useMemo(() => {
      if (multiple) {
        return Array.isArray(filterValue) && filterValue.length > 0
          ? `${filterValue.length} ${t(TextCodes.Base.Selected)}`
          : '';
      } else {
        return options.find((option) => option.value === filterValue)?.label || filterValue;
      }
    }, [multiple, options, filterValue]);

    const handleFilterToggle = () => {
      setOpen((previousOpen) => !previousOpen);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (readOnly) return;
      setAnchorEl(event.currentTarget);
      setOpen((previousOpen) => !previousOpen);
    };

    const handleSelect =
      ({ value, label }: { value: T; label: string }) =>
      () => {
        if (multiple) {
          if (Array.isArray(filterValue) && filterValue.includes(value)) {
            getValue(filterValue.filter((item) => item !== value));
          } else {
            getValue([...(Array.isArray(filterValue) ? filterValue : []), value] as T[]);
          }
        } else {
          getValue(value);
          handleFilterToggle();
        }
      };

    const handleRemoveClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      getValue((multiple ? [] : '') as T | T[]);
    };

    return (
      <FormControl fullWidth>
        <TextField
          {...props}
          ref={ref}
          value={displayValue}
          sx={{
            '& .MuiInputBase-input': {
              cursor: 'pointer',
              textOverflow: 'ellipsis',
            },
          }}
          label={label}
          variant='outlined'
          fullWidth
          onClick={handleClick}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          InputProps={{
            endAdornment: isLoading ? (
              <InputAdornment position='end'>
                <CircularProgress size={20} color='primary' />
              </InputAdornment>
            ) : (
              <InputAdornment position='end'>
                {withRemoveButton && !props?.disabled ? (
                  <IconButton
                    sx={{
                      p: '4px',
                      display: 'flex',
                      visibility: (isFocused || isHovered) && displayValue ? 'unset' : 'hidden',
                    }}
                    size='small'
                    onClick={handleRemoveClick}
                  >
                    <ClearIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                ) : null}
                {!props?.disabled ? (
                  <IconButton sx={{ p: '2px' }}>
                    {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
            inputProps: { readOnly: true },
          }}
        />
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement='bottom-end'
          sx={{ zIndex: dropdownZIndex }}
        >
          <ClickAwayListener onClickAway={handleFilterToggle}>
            <Paper
              sx={{
                width: anchorEl ? anchorEl.clientWidth : undefined,
                overflow: 'auto',
              }}
            >
              <List sx={{ maxHeight: 300 }}>
                {options.map((option) => {
                  return option.value && option.label ? (
                    <ListItem
                      key={option.value as string}
                      value={option.value as string}
                      dense
                      onClick={handleSelect(option)}
                      sx={{
                        backgroundColor:
                          !multiple && displayValue === option.label
                            ? 'rgba(0, 0, 0, 0.08)'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                      }}
                    >
                      {multiple && (
                        <Checkbox
                          edge='start'
                          checked={isChecked(option.value)}
                          tabIndex={-1}
                          disableRipple
                        />
                      )}
                      <ListItemText primary={option.label} sx={{ cursor: 'pointer' }} />
                    </ListItem>
                  ) : null;
                })}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </FormControl>
    );
  }
);
